import React, { useMemo } from "react";
import { Controller } from "react-hook-form";
import { FormInput } from "@/components/bnp-ui";
import {
  getFieldsByCategoryAndType,
  getPropertyTypeLabel,
  getPropertyCategoryLabel,
} from "./config/propertyCategories";
import {
  GeneralAmenity,
  ResidentialAmenity,
  CommercialAmenity,
  IndustrialAmenity,
  SpecialPurposeAmenity,
  LandAmenity,
  PropertyCategory,
} from "@/types/property-config";

interface CategorySpecificFieldsProps {
  category: string;
  propertyType: string;
  register: any;
  errors: any;
  control: any;
  watch: any;
}

const FIELD_LABELS: Record<string, string> = {
  bedroom_count: "Bedroom Count",
  bathroom_count: "Bathroom Count",
  property_size_sqm: "Property Size (sqm)",
  number_of_floors: "Number of Floors",
  parking_spaces: "Parking Spaces",
  newly_built: "Newly Built",
  floor_area_sqm: "Floor Area (sqm)",
  floor_number: "Floor Number",
  number_of_private_offices: "Number of Private Offices",
  meeting_rooms: "Meeting Rooms",
  conference_rooms: "Conference Rooms",
  reception_area: "Reception Area",
  open_workspace_capacity: "Open Workspace Capacity",
  parking_capacity: "Parking Capacity",
  dedicated_desks: "Dedicated Desks",
  private_offices: "Private Offices",
  capacity: "Capacity",
  internet_speed: "Internet Speed",
  printing_available: "Printing Available",
  reception: "Reception",
  kitchen: "Kitchen",
  lounge: "Lounge",
  shop_size_sqm: "Shop Size (sqm)",
  storage_room: "Storage Room",
  electricity: "Electricity",
  water_supply: "Water Supply",
  mall_name: "Mall Name",
  floor_level: "Floor Level",
  nearest_entrance: "Nearest Entrance",
  operating_hours: "Operating Hours",
  central_air_conditioning: "Central Air Conditioning",
  escalator: "Escalator",
  backup_generator: "Backup Generator",
  cctv: "CCTV",
  security: "Security",
  fire_alarm_system: "Fire Alarm System",
  emergency_exit: "Emergency Exit",
  public_toilets: "Public Toilets",
  total_floors: "Total Floors",
  total_units: "Total Units",
  gross_floor_area: "Gross Floor Area (sqm)",
  occupancy_status: "Occupancy Status",
  current_rental_income: "Current Rental Income",
  mixed_use: "Mixed-use",
  plaza_name: "Plaza Name",
  number_of_units: "Number of Units / Shops",
  available_unit_sizes: "Available Unit Size(s)",
  number_of_vacant_units: "Number of Vacant Units",
  shop_area_sqm: "Shop Area (sqm)",
  frontage_width: "Frontage Width",
  ceiling_height: "Ceiling Height",
  display_window: "Display Window",
  changing_room: "Changing Room",
  display_area_sqm: "Display Area (sqm)",
  office_area: "Office Area",
  storage_area: "Storage Area",
  glass_frontage_length: "Glass Frontage Length",
  number_of_floors_field: "Number of Floors",
  dining_capacity: "Dining Capacity",
  kitchen_size: "Kitchen Size",
  indoor_seating: "Indoor Seating",
  outdoor_seating: "Outdoor Seating",
  number_of_restrooms: "Number of Restrooms",
  private_dining_rooms: "Private Dining Rooms",
  bar_area: "Bar Area",
  number_of_rooms: "Number of Rooms",
  room_types: "Room Types",
  number_of_suites: "Number of Suites",
  conference_rooms_count: "Conference Rooms",
  banquet_hall: "Banquet Hall",
  reception_area_field: "Reception Area",
  hall_capacity: "Hall Capacity",
  number_of_event_halls: "Number of Event Halls",
  vip_lounge: "VIP Lounge",
  bridal_room: "Bridal Room",
  waiting_lounge: "Waiting Lounge",
  dressing_room: "Dressing Room",
  number_of_toilets: "Number of Toilets",
  stage: "Stage",
  led_screen: "LED Screen",
  sound_system: "Sound System",
  lighting: "Lighting",
  wheelchair_access: "Wheelchair Access",
  warehouse_size: "Warehouse Size (sqm)",
  loading_bays: "Loading Bays",
  dock_levelers: "Dock Levelers",
  truck_access: "Truck Access",
  container_access: "Container Access",
  office_attached: "Office Attached",
  power_capacity: "Power Capacity",
  cold_storage: "Cold Storage",
  fire_safety_system: "Fire Safety System",
  factory_floor_area: "Factory Floor Area",
  warehouse_area: "Warehouse Area",
  number_of_production_floors: "Number of Production Floors",
  floor_load_capacity: "Floor Load Capacity",
  dock_doors: "Dock Doors",
  transformer_capacity: "Transformer Capacity",
  generator_capacity: "Generator Capacity",
  borehole: "Borehole",
  gas_supply: "Gas Supply",
  waste_treatment_facility: "Waste Treatment Facility",
  workshop_area: "Workshop Area",
  yard_space: "Yard Space",
  floor_strength: "Floor Strength",
  three_phase_power: "Three-phase Power",
  compressed_air_lines: "Compressed Air Lines",
  cold_storage_capacity: "Cold Storage Capacity",
  number_of_cold_rooms: "Number of Cold Rooms",
  temperature_range: "Temperature Range",
  freezer_capacity: "Freezer Capacity",
  chiller_capacity: "Chiller Capacity",
  loading_area: "Loading Area",
  yard_size: "Yard Size",
  number_of_dock_doors: "Number of Dock Doors",
  truck_parking_capacity: "Truck Parking Capacity",
  trailer_parking_capacity: "Trailer Parking Capacity",
  fleet_parking: "Fleet Parking",
  container_holding_area: "Container Holding Area",
  number_of_classrooms: "Number of Classrooms",
  student_capacity: "Student Capacity",
  labs: "Labs",
  library: "Library",
  sports_facilities: "Sports Facilities",
  number_of_beds: "Number of Beds",
  number_of_wards: "Number of Wards",
  operating_theatres: "Operating Theatres",
  emergency_unit: "Emergency Unit",
  pharmacy: "Pharmacy",
  seating_capacity: "Seating Capacity",
  ablution_area: "Ablution Area",
  number_of_pumps: "Number of Pumps",
  storage_tank_capacity: "Storage Tank Capacity",
  convenience_store: "Convenience Store",
  car_wash: "Car Wash",
  farm_size_hectares: "Farm Size (hectares)",
  type_of_farming: "Type of Farming",
  irrigation_system: "Irrigation System",
  storage_facility: "Storage Facility",
  building_type: "Building Type",
  total_area: "Total Area",
  security_level: "Security Level",
};

const BOOLEAN_FIELDS = new Set([
  "newly_built",
  "furnished",
  "serviced",
  "shared",
  "reception_area",
  "printing_available",
  "storage_room",
  "electricity",
  "water_supply",
  "central_air_conditioning",
  "escalator",
  "backup_generator",
  "cctv",
  "security",
  "fire_alarm_system",
  "emergency_exit",
  "public_toilets",
  "mixed_use",
  "changing_room",
  "indoor_seating",
  "outdoor_seating",
  "bar_area",
  "banquet_hall",
  "vip_lounge",
  "bridal_room",
  "waiting_lounge",
  "dressing_room",
  "stage",
  "led_screen",
  "sound_system",
  "lighting",
  "wheelchair_access",
  "dock_levelers",
  "truck_access",
  "container_access",
  "office_attached",
  "cold_storage",
  "fire_safety_system",
  "borehole",
  "gas_supply",
  "waste_treatment_facility",
  "three_phase_power",
  "compressed_air_lines",
  "fleet_parking",
  "container_holding_area",
  "labs",
  "library",
  "sports_facilities",
  "emergency_unit",
  "pharmacy",
  "ablution_area",
  "convenience_store",
  "car_wash",
  "irrigation_system",
  "storage_facility",
]);

const CATEGORY_TO_PROPERTY_CATEGORY: Record<string, PropertyCategory> = {
  residential: PropertyCategory.RESIDENTIAL,
  commercial: PropertyCategory.COMMERCIAL,
  industrial: PropertyCategory.INDUSTRIAL,
  special_purpose: PropertyCategory.SPECIAL_PURPOSE,
  land: PropertyCategory.LAND,
};
const CategorySpecificFields: React.FC<CategorySpecificFieldsProps> = ({
  category,
  propertyType,
  register,
  errors,
  control,
  watch,
}) => {
  // 1. All hooks called unconditionally at the top level
  const watchedAmenities = watch("amenities") || [];

  const availableAmenities = useMemo(() => {
    if (!category || category === "land") return [];

    const pc = CATEGORY_TO_PROPERTY_CATEGORY[category];
    if (!pc) return [];

    const general = Object.values(GeneralAmenity);
    let categorySpecific: string[] = [];

    switch (pc) {
      case PropertyCategory.RESIDENTIAL:
        categorySpecific = Object.values(ResidentialAmenity);
        break;
      case PropertyCategory.COMMERCIAL:
        categorySpecific = Object.values(CommercialAmenity);
        break;
      case PropertyCategory.INDUSTRIAL:
        categorySpecific = Object.values(IndustrialAmenity);
        break;
      case PropertyCategory.SPECIAL_PURPOSE:
        categorySpecific = Object.values(SpecialPurposeAmenity);
        break;
      case PropertyCategory.LAND:
        categorySpecific = Object.values(LandAmenity);
        break;
    }
    return Array.from(new Set([...general, ...categorySpecific]));
  }, [category]);

  // 2. Conditional check happens AFTER all hooks are defined
  if (!category || category === "land") {
    return null;
  }

  const fields = getFieldsByCategoryAndType(category, propertyType);

  const renderField = (field: string) => {
    const label =
      FIELD_LABELS[field] ||
      field.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

    if (field === "room_types") {
      return (
        <div key={field} className="flex flex-col gap-1">
          <label className="text-sm font-medium text-main-900">{label}</label>
          <input
            type="text"
            {...register(field)}
            placeholder="e.g. Single, Double, Suite"
            className="w-full px-4 py-3 text-gray-600 bg-white shadow-sm border border-soft-200 rounded-lg focus:outline-none focus:border-bnp-cyan transition-colors"
          />
          {errors?.[field] && (
            <p className="text-red-500 text-sm">
              {errors[field].message as string}
            </p>
          )}
        </div>
      );
    }

    if (field === "internet_speed") {
      return (
        <div key={field} className="flex flex-col gap-1">
          <label className="text-sm font-medium text-main-900">{label}</label>
          <input
            type="text"
            {...register(field)}
            placeholder="e.g. 100 Mbps"
            className="w-full px-4 py-3 text-gray-600 bg-white shadow-sm border border-soft-200 rounded-lg focus:outline-none focus:border-bnp-cyan transition-colors"
          />
          {errors?.[field] && (
            <p className="text-red-500 text-sm">
              {errors[field].message as string}
            </p>
          )}
        </div>
      );
    }

    if (field === "occupancy_status") {
      return (
        <div key={field} className="flex flex-col gap-1">
          <label className="text-sm font-medium text-main-900">{label}</label>
          <select
            {...register(field)}
            className="w-full px-4 py-3 text-gray-600 bg-white shadow-sm border border-soft-200 rounded-lg focus:outline-none focus:border-bnp-cyan transition-colors"
          >
            <option value="">Select status</option>
            <option value="occupied">Occupied</option>
            <option value="partially-occupied">Partially Occupied</option>
            <option value="vacant">Vacant</option>
          </select>
          {errors?.[field] && (
            <p className="text-red-500 text-sm">
              {errors[field].message as string}
            </p>
          )}
        </div>
      );
    }

    if (field === "temperature_range") {
      return (
        <div key={field} className="flex flex-col gap-1">
          <label className="text-sm font-medium text-main-900">{label}</label>
          <input
            type="text"
            {...register(field)}
            placeholder="e.g. -20°C to -5°C"
            className="w-full px-4 py-3 text-gray-600 bg-white shadow-sm border border-soft-200 rounded-lg focus:outline-none focus:border-bnp-cyan transition-colors"
          />
          {errors?.[field] && (
            <p className="text-red-500 text-sm">
              {errors[field].message as string}
            </p>
          )}
        </div>
      );
    }

    if (field === "type_of_farming") {
      return (
        <div key={field} className="flex flex-col gap-1">
          <label className="text-sm font-medium text-main-900">{label}</label>
          <input
            type="text"
            {...register(field)}
            placeholder="e.g. Poultry, Crop, Mixed"
            className="w-full px-4 py-3 text-gray-600 bg-white shadow-sm border border-soft-200 rounded-lg focus:outline-none focus:border-bnp-cyan transition-colors"
          />
          {errors?.[field] && (
            <p className="text-red-500 text-sm">
              {errors[field].message as string}
            </p>
          )}
        </div>
      );
    }

    if (field === "building_type") {
      return (
        <div key={field} className="flex flex-col gap-1">
          <label className="text-sm font-medium text-main-900">{label}</label>
          <input
            type="text"
            {...register(field)}
            placeholder="e.g. Office Complex, Warehouse"
            className="w-full px-4 py-3 text-gray-600 bg-white shadow-sm border border-soft-200 rounded-lg focus:outline-none focus:border-bnp-cyan transition-colors"
          />
          {errors?.[field] && (
            <p className="text-red-500 text-sm">
              {errors[field].message as string}
            </p>
          )}
        </div>
      );
    }

    if (field === "security_level") {
      return (
        <div key={field} className="flex flex-col gap-1">
          <label className="text-sm font-medium text-main-900">{label}</label>
          <select
            {...register(field)}
            className="w-full px-4 py-3 text-gray-600 bg-white shadow-sm border border-soft-200 rounded-lg focus:outline-none focus:border-bnp-cyan transition-colors"
          >
            <option value="">Select level</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="maximum">Maximum</option>
          </select>
          {errors?.[field] && (
            <p className="text-red-500 text-sm">
              {errors[field].message as string}
            </p>
          )}
        </div>
      );
    }

    if (BOOLEAN_FIELDS.has(field)) {
      return (
        <div key={field} className="flex items-center gap-2">
          <input
            type="checkbox"
            {...register(field)}
            className="w-4 h-4 text-bnp-cyan border-gray-300 rounded focus:ring-bnp-cyan"
          />
          <label className="text-sm font-medium text-main-900">{label}</label>
        </div>
      );
    }

    return (
      <div key={field} className="flex flex-col gap-1">
        <label className="text-sm font-medium text-main-900">{label}</label>
        <input
          type="number"
          {...register(field, { valueAsNumber: true })}
          placeholder={`Enter ${label.toLowerCase()}`}
          className="w-full px-4 py-3 text-gray-600 bg-white shadow-sm border border-soft-200 rounded-lg focus:outline-none focus:border-bnp-cyan transition-colors"
        />
        {errors?.[field] && (
          <p className="text-red-500 text-sm">
            {errors[field].message as string}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="mt-6 space-y-4">
      <h3 className="font-bold text-base text-main-900">
        {getPropertyCategoryLabel(category)} Details
      </h3>
      <p className="text-sm text-grey-7">
        {getPropertyTypeLabel(propertyType)}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {fields.map((field) => renderField(field))}
      </div>
    </div>
  );
};
export default CategorySpecificFields;
